import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, FileText, AlertCircle } from 'lucide-react';
import { usageCapApplies, hasReachedLimit, incrementUsage } from '../../lib/webUsage';
import FreeTierBanner from '../FreeTierBanner';

// Caps from spec: 50MB soft (warn), 100MB hard (refuse), 150MB combined hard.
const SIZE_WARN = 50 * 1024 * 1024;
const SIZE_HARD = 100 * 1024 * 1024;
const COMBINED_HARD = 150 * 1024 * 1024;

// Mobile soft cap from spec.
function isMobileLike() {
  if (typeof navigator === 'undefined') return false;
  if (navigator.deviceMemory && navigator.deviceMemory < 4) return true;
  return /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
}
const MOBILE_SIZE_WARN = 20 * 1024 * 1024;

export default function CompareUploader({ onReady }) {
  const { t } = useTranslation();
  const [file1, setFile1] = useState(null);
  const [file2, setFile2] = useState(null);
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState(null);
  const [capReached, setCapReached] = useState(false);

  function chooseFile(setter, side) {
    return (file) => {
      setError(null);
      setWarning(null);
      if (!file) return;
      if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
        setError(t('tools:compare.error_not_pdf', 'Please drop a PDF file.'));
        return;
      }
      if (file.size > SIZE_HARD) {
        setError(t('tools:compare.file_too_large', 'File too large. Try compressing first.'));
        return;
      }
      const softCap = isMobileLike() ? MOBILE_SIZE_WARN : SIZE_WARN;
      if (file.size > softCap) {
        setWarning(t('tools:compare.large_file_warning', 'Large file — compare may take 30+ seconds. You can cancel anytime.'));
      }
      setter(file);
    };
  }

  function attemptStart(a, b) {
    if (!a || !b) return;
    if (a.size + b.size > COMBINED_HARD) {
      setError(t('tools:compare.combined_too_large', 'Total size too large. Compare smaller files.'));
      return;
    }
    if (usageCapApplies() && hasReachedLimit()) {
      setCapReached(true);
      return;
    }
    if (usageCapApplies()) incrementUsage();
    onReady({ file1: a, file2: b });
  }

  return (
    <div className="max-w-4xl mx-auto px-6 py-12">
      {/* FreeTierBanner is a modal — it needs `open` to render anything. The
          earlier version returned `<FreeTierBanner />` from an early return,
          which evaluated to null and left the page blank when the user hit
          the free-tier daily cap (3/day). Now we render the uploader normally
          and overlay the modal on top so the user sees their dropzones behind
          the dialog. */}
      <FreeTierBanner open={capReached} onClose={() => setCapReached(false)} />

      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900">
          {t('tools:compare.title', 'Compare PDFs')}
        </h1>
        <p className="mt-2 text-gray-600">
          {t('tools:compare.description', 'Spot differences between two PDF versions side-by-side.')}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Dropzone
          label={t('tools:compare.uploader_v1', 'Drop original PDF here')}
          file={file1}
          onPick={(f) => { const set = chooseFile(setFile1, 'left'); set(f); attemptStart(f, file2); }}
        />
        <Dropzone
          label={t('tools:compare.uploader_v2', 'Drop new version here')}
          file={file2}
          onPick={(f) => { const set = chooseFile(setFile2, 'right'); set(f); attemptStart(file1, f); }}
        />
      </div>

      {warning && (
        <div className="mt-4 flex items-start gap-2 rounded-lg bg-yellow-50 border border-yellow-200 p-3 text-sm text-yellow-800">
          <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
          <span>{warning}</span>
        </div>
      )}
      {error && (
        <div role="alert" className="mt-4 flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-800">
          <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}

function Dropzone({ label, file, onPick }) {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => {
        e.preventDefault(); setDrag(false);
        const f = e.dataTransfer.files?.[0];
        if (f) onPick(f);
      }}
      onClick={() => inputRef.current?.click()}
      className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all min-h-[200px] flex flex-col items-center justify-center ${
        drag ? 'border-indigo-500 bg-indigo-50' : file ? 'border-emerald-300 bg-emerald-50/30' : 'border-gray-300 hover:border-indigo-300 hover:bg-indigo-50/30'
      }`}
    >
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,.pdf"
        className="hidden"
        onChange={(e) => onPick(e.target.files?.[0])}
      />
      {file ? (
        <>
          <FileText size={32} className="text-emerald-600 mb-3" />
          <p className="text-sm font-medium text-gray-900">{file.name}</p>
          <p className="text-xs text-gray-500 mt-1">{Math.round(file.size / 1024)} KB</p>
        </>
      ) : (
        <>
          <Upload size={32} className="text-gray-400 mb-3" />
          <p className="text-sm text-gray-600">{label}</p>
        </>
      )}
    </div>
  );
}
